import {parse as parseDate} from 'date-fns';
import {parse as xmlParse} from 'fast-xml-parser';

import {CURRENCY} from 'core/constants/currency';
import {TRANSPORT_PACKAGE_TYPES} from 'types/gtins';
import {AllowanceCharge, InfoValues, InvoiceLine, PageValues, TaxesAndTotal} from 'types/invoices';

import {toDisplayTaxCategory, VAT_CATEGORY} from './consts';

function parseDDMMYYYY(dateStr: string | undefined): any {
    if (!dateStr) return '';
    try {
        const parsed = parseDate(dateStr, 'yyyy-MM-dd', new Date());
        return isNaN(parsed.getTime()) ? '' : parsed;
    } catch {
        return '';
    }
}

function safeGet(obj: any, path: string, fallback: any = undefined) {
    return path
        .split('.')
        .reduce((cur, key) => (cur && cur[key] !== undefined ? cur[key] : fallback), obj);
}

function getText(node: any): string {
    if (node == null) return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (typeof node === 'object' && node['#text'] !== undefined) return String(node['#text']);
    return '';
}

function getAttr(node: any, attrName: string): string | undefined {
    if (node && typeof node === 'object') {
        return node[`@_${attrName}`];
    }
    return undefined;
}

function removeNAfromAddress(address: string) {
    if (address === 'NOT_AVAILABLE') {
        return '';
    }

    return address;
}

export const parseXmlToData = (xml: string) => {
    const options = {
        ignoreAttributes: false,
        parseAttributeValue: false,
        parseTagValue: false,
        parseNodeValue: true,
        numParseOptions: {
            hex: true,
            leadingZeros: false,
        },
    };

    const parsed = xmlParse(xml, options);
    const inv = parsed?.Invoice;
    if (!inv) throw new Error('Invalid XML: missing Invoice root');

    // PageValues
    const pageValues: PageValues = {
        periodFrom: parseDDMMYYYY(getText(safeGet(inv, 'cac:InvoicePeriod.cbc:StartDate'))),
        periodTo: parseDDMMYYYY(getText(safeGet(inv, 'cac:InvoicePeriod.cbc:EndDate'))),
        taxPointDate: parseDDMMYYYY(getText(safeGet(inv, 'cbc:TaxPointDate'))),
        buyerReference: getText(safeGet(inv, 'cbc:BuyerReference')),
        contractDocumentReference: getText(safeGet(inv, 'cac:ContractDocumentReference.cbc:ID')),
        purchaseOrderReference: getText(safeGet(inv, 'cac:OrderReference.cbc:ID')),
        salesOrderReference: getText(safeGet(inv, 'cac:OrderReference.cbc:SalesOrderID')),
    };

    // InfoValues
    const supplierParty = safeGet(inv, 'cac:AccountingSupplierParty.cac:Party', {});
    const customerParty = safeGet(inv, 'cac:AccountingCustomerParty.cac:Party', {});
    const paymentMeansNode = safeGet(inv, 'cac:PaymentMeans');
    const payeeFinancialAccount = safeGet(paymentMeansNode, 'cac:PayeeFinancialAccount', {});

    // customerCompanyNameOrTIN
    const customerLegalEntity = safeGet(customerParty, 'cac:PartyLegalEntity');
    let customerCompanyNameOrTIN: {value: string; label: string} = {value: '', label: ''};
    if (customerLegalEntity) {
        const regName = getText(safeGet(customerLegalEntity, 'cbc:RegistrationName'));
        const companyIdNode = safeGet(customerLegalEntity, 'cbc:CompanyID');
        const companyId = getText(companyIdNode);
        customerCompanyNameOrTIN = {value: companyId, label: regName};
    }

    const customerCity = removeNAfromAddress(
        getText(safeGet(customerParty, 'cac:PostalAddress.cbc:CityName')),
    );
    const customerZip = removeNAfromAddress(
        getText(safeGet(customerParty, 'cac:PostalAddress.cbc:PostalZone')),
    );
    const customerCountry = removeNAfromAddress(
        getText(safeGet(customerParty, 'cac:PostalAddress.cac:Country.cbc:IdentificationCode')),
    );
    const customerStreet = removeNAfromAddress(
        getText(safeGet(customerParty, 'cac:PostalAddress.cbc:StreetName')),
    );

    const infoValues: InfoValues = {
        irn: getText(safeGet(inv, 'cbc:ID')),
        issueDate: parseDDMMYYYY(getText(safeGet(inv, 'cbc:IssueDate'))),
        dueDate: parseDDMMYYYY(getText(safeGet(inv, 'cbc:DueDate'))),
        companyNameAndTIN: getText(
            safeGet(supplierParty, 'cac:PartyLegalEntity.cbc:RegistrationName'),
        ),
        companyTIN: getText(safeGet(supplierParty, 'cac:PartyLegalEntity.cbc:CompanyID')),
        iban: getText(safeGet(payeeFinancialAccount, 'cbc:ID')),
        bic: getText(safeGet(payeeFinancialAccount, 'cac:FinancialInstitutionBranch.cbc:ID')),

        supplierStreetName: removeNAfromAddress(
            getText(safeGet(supplierParty, 'cac:PostalAddress.cbc:StreetName')),
        ),
        supplierCityName: removeNAfromAddress(
            getText(safeGet(supplierParty, 'cac:PostalAddress.cbc:CityName')),
        ),
        supplierPostalZone: removeNAfromAddress(
            getText(safeGet(supplierParty, 'cac:PostalAddress.cbc:PostalZone')),
        ),
        supplierCountry: removeNAfromAddress(
            getText(safeGet(supplierParty, 'cac:PostalAddress.cac:Country.cbc:IdentificationCode')),
        ),

        supplierPhoneNumber: getText(safeGet(supplierParty, 'cac:Contact.cbc:Telephone')),
        supplierEmail: getText(safeGet(supplierParty, 'cac:Contact.cbc:ElectronicMail')),
        customerPhoneNumber: getText(safeGet(customerParty, 'cac:Contact.cbc:Telephone')),
        customerEmail: getText(safeGet(customerParty, 'cac:Contact.cbc:ElectronicMail')),
        storageLocationAddress: getText(
            safeGet(inv, 'cac:Delivery.cac:DeliveryLocation.cac:Address.cbc:StreetName'),
        ),
        storageLocationName: {
            label: getText(
                safeGet(
                    inv,
                    'cac:Delivery.cac:DeliveryLocation.cac:Address.cbc:AdditionalStreetName',
                ),
            ),
            value: safeGet(inv, 'cac:Delivery.cac:DeliveryLocation.cac:Address.cbc:StreetName'),
        },
        customerCompanyNameOrTIN,
        customerCity,
        customerZip,
        customerCountry,
        customerStreet,
        customerAddress: `${customerZip} ${customerCountry} ${customerCity} ${customerStreet}`,
    };

    // TaxSubtotal > calculationsByTaxCategory
    const taxSubtotalsRaw = safeGet(inv, 'cac:TaxTotal.cac:TaxSubtotal', []);
    const taxSubtotals = Array.isArray(taxSubtotalsRaw) ? taxSubtotalsRaw : [taxSubtotalsRaw];
    const calculationsByTaxCategory = taxSubtotals.map((sub: any) => {
        const category = getText(safeGet(sub, 'cac:TaxCategory.cbc:ID'));
        const rate = getText(safeGet(sub, 'cac:TaxCategory.cbc:Percent'));
        const reason = getText(safeGet(sub, 'cac:TaxCategory.cbc:TaxExemptionReason'));
        return {
            taxCategory: `${category} - ${rate}`,
            taxCategoryDisplay: toDisplayTaxCategory(category),
            rate,
            taxableAmount: getText(safeGet(sub, 'cbc:TaxableAmount')),
            taxableAmountDisplay: getText(safeGet(sub, 'cbc:TaxableAmount')),
            taxAmount: getText(safeGet(sub, 'cbc:TaxAmount')),
            taxAmountDisplay: getText(safeGet(sub, 'cbc:TaxAmount')),
            taxExemptionReason: reason,
        };
    });

    const chrgs: AllowanceCharge[] = safeGet(inv, 'cac:AllowanceCharge', []);
    const chrgs2 = Array.isArray(chrgs) ? chrgs : [chrgs];
    const qwe = chrgs2.map(
        (i) =>
            ({
                isCharge: safeGet(i, 'cbc:ChargeIndicator') ? 'charge' : 'allowance',
                reason: getText(safeGet(i, 'cbc:AllowanceChargeReason')),
                rate: getText(safeGet(i, 'cbc:MultiplierFactorNumeric')) ?? '-',
                baseAmount: getText(safeGet(i, 'cbc:BaseAmount')) ?? '-',
                totalAmount: getText(safeGet(i, 'cbc:Amount')),
                vatCategory: {
                    label: toDisplayTaxCategory(getText(safeGet(i, 'cac:TaxCategory.cbc:ID'))),
                    value: getText(safeGet(i, 'cac:TaxCategory.cbc:ID')),
                },
                vat: getText(safeGet(i, 'cac:TaxCategory.cbc:Percent')),
            } as AllowanceCharge),
    );

    const taxesAndTotal: TaxesAndTotal = {
        calculationsByTaxCategory,
        taxTotal: getText(safeGet(inv, 'cac:TaxTotal.cbc:TaxAmount')),
        // chargesRadio:
        //     getText(safeGet(inv, 'cac:AllowanceCharge.cbc:ChargeIndicator')) === 'true'
        //         ? 'true'
        //         : 'false',
        // charges: [
        //     {
        //         charge:
        //             getText(safeGet(inv, 'cac:AllowanceCharge.cbc:ChargeIndicator')) === 'true'
        //                 ? 'true'
        //                 : 'false',
        //         value: getText(safeGet(inv, 'cac:AllowanceCharge.cbc:Amount')),
        //     },
        // ],
        allowanceCharge: qwe,
        baseAmount: '', // не сохраняется
        multiplierFactorNumeric: '', // не сохраняется
        whtChargeAmount_1: getText(safeGet(inv, 'cac:AllowanceCharge.cbc:Amount')),
        lineExtensionAmount: getText(
            safeGet(inv, 'cac:LegalMonetaryTotal.cbc:LineExtensionAmount'),
        ),
        taxExclusiveAmount: getText(safeGet(inv, 'cac:LegalMonetaryTotal.cbc:TaxExclusiveAmount')),
        taxExclusiveAmountDisplay: getText(
            safeGet(inv, 'cac:LegalMonetaryTotal.cbc:TaxExclusiveAmount'),
        ),
        taxInclusiveAmount: getText(safeGet(inv, 'cac:LegalMonetaryTotal.cbc:TaxInclusiveAmount')),
        taxInclusiveAmountDisplay: getText(
            safeGet(inv, 'cac:LegalMonetaryTotal.cbc:TaxInclusiveAmount'),
        ),
        allowanceTotalAmount: getText(
            safeGet(inv, 'cac:LegalMonetaryTotal.cbc:AllowanceTotalAmount'),
        ),
        allowanceTotalAmountDisplay: getText(
            safeGet(inv, 'cac:LegalMonetaryTotal.cbc:AllowanceTotalAmount'),
        ),
        chargeTotalAmount: getText(safeGet(inv, 'cac:LegalMonetaryTotal.cbc:ChargeTotalAmount')),
        chargeTotalAmountDisplay: getText(
            safeGet(inv, 'cac:LegalMonetaryTotal.cbc:ChargeTotalAmount'),
        ),
        payableRoundingAmount: getText(
            safeGet(inv, 'cac:LegalMonetaryTotal.cbc:PayableRoundingAmount'),
        ),
        prepaidAmount: getText(safeGet(inv, 'cac:LegalMonetaryTotal.cbc:PrepaidAmount')),
        payableAmount: getText(safeGet(inv, 'cac:LegalMonetaryTotal.cbc:PayableAmount')),
        payableAmountDisplay: getText(safeGet(inv, 'cac:LegalMonetaryTotal.cbc:PayableAmount')),
        withholdingTax: getText(safeGet(inv, 'cbc:Note')),
    };

    // InvoiceLine
    const linesRaw = safeGet(inv, 'cac:InvoiceLine', []);
    const linesArray = Array.isArray(linesRaw) ? linesRaw : [linesRaw];
    const invoiceLine: InvoiceLine[] = linesArray.map((line: any) => {
        const item = safeGet(line, 'cac:Item', {});
        const classification = safeGet(item, 'cac:ClassifiedTaxCategory', {});
        const priceNode = safeGet(line, 'cac:Price.cbc:PriceAmount');
        const qtyNode = safeGet(line, 'cbc:InvoicedQuantity');
        const gtinNode = safeGet(item, 'cac:StandardItemIdentification.cbc:ID');
        const additionalItemProperty = safeGet(item, 'cac:AdditionalItemProperty', []);

        const commodityClassification = safeGet(item, 'cac:CommodityClassification', []);
        const additionalItemPropertyArray = !additionalItemProperty
            ? []
            : Array.isArray(additionalItemProperty)
            ? additionalItemProperty
            : [additionalItemProperty];
        const markingCodeNode = additionalItemPropertyArray.find(
            (i: {'cbc:Name': string; 'cbc:Value': string}) =>
                i['cbc:Name'] === 'markingCode' || i['cbc:Name'] === 'transportCode',
        );

        const lineAmountWithVat = additionalItemPropertyArray.find(
            (i: {'cbc:Name': string; 'cbc:Value': string}) =>
                i['cbc:Name'] === `Line Amount with VAT (${CURRENCY})`,
        );

        const productCodeDescription = additionalItemPropertyArray.find(
            (i: {'cbc:Name': string; 'cbc:Value': string}) =>
                i['cbc:Name'] === 'productCodeDescription',
        );

        const combinedCodeId = additionalItemPropertyArray.find(
            (i: {'cbc:Name': string; 'cbc:Value': string}) => i['cbc:Name'] === 'combinedCodeId',
        );

        const isCombined = additionalItemPropertyArray.find(
            (i: {'cbc:Name': string; 'cbc:Value': string}) => i['cbc:Name'] === 'isCombined',
        );

        const packagingType = additionalItemPropertyArray.find(
            (i: {'cbc:Name': string; 'cbc:Value': string}) => i['cbc:Name'] === 'packagingType',
        );

        const parent = additionalItemPropertyArray.find(
            (i: {'cbc:Name': string; 'cbc:Value': string}) => i['cbc:Name'] === 'parent',
        );

        const level = additionalItemPropertyArray.find(
            (i: {'cbc:Name': string; 'cbc:Value': string}) => i['cbc:Name'] === 'level',
        );

        const vatCategoryValue = getText(safeGet(classification, 'cbc:ID'));
        const vatCategoryLabel =
            VAT_CATEGORY.find((i) => i.value === vatCategoryValue)?.label ?? '';

        let serviceCategory;
        let isHsn;
        if (Array.isArray(commodityClassification)) {
            const serviceCategoryRaw = commodityClassification.find(
                (i: {'#text': string; '@_listID': string}) => {
                    const id = getAttr(safeGet(i, 'cbc:ItemClassificationCode'), 'listID');
                    return id === 'MP - Product';
                },
            );

            serviceCategoryRaw &&
                (serviceCategory = getText(
                    safeGet(serviceCategoryRaw, 'cbc:ItemClassificationCode'),
                ));
        } else {
            serviceCategory = getText(
                safeGet(commodityClassification, 'cbc:ItemClassificationCode'),
            );
            isHsn = getAttr(
                safeGet(commodityClassification, 'cbc:ItemClassificationCode'),
                'listID',
            );

            // const commodityClassificationPrefix = id === 'HS - Harmonised system' ? 'SAC' : 'HS';
            // serviceCategory = `${commodityClassificationPrefix} - ${getText(
            //     safeGet(commodityClassification, 'cbc:ItemClassificationCode'),
            // )}`;
        }

        const allowanceCharge = safeGet(line, 'cac:AllowanceCharge');
        const chargeIndicator = safeGet(allowanceCharge, 'cbc:ChargeIndicator') ? 'true' : 'false';
        const reason = getText(safeGet(allowanceCharge, 'cbc:AllowanceChargeReason'));
        const ratePercent = Number(
            getText(safeGet(allowanceCharge, 'cbc:MultiplierFactorNumeric')),
        );
        const amountXaf = Number(getText(safeGet(allowanceCharge, 'cbc:Amount')));
        const baseAmount = Number(getText(safeGet(allowanceCharge, 'cbc:BaseAmount')));

        return {
            ...(allowanceCharge && {
                charges: {
                    discountRateAndBaseAmount: !!ratePercent,
                    isCharge: chargeIndicator,
                    reason,
                    amountXaf,
                    ...(!!baseAmount && {
                        baseAmount,
                        ratePercent,
                    }),
                },
            }),
            description: getText(safeGet(item, 'cbc:Description')),
            buyersItemId: getText(safeGet(item, 'cac:BuyersItemIdentification.cbc:ID')),
            sellersItemId: getText(safeGet(item, 'cac:SellersItemIdentification.cbc:ID')),
            gtin:
                packagingType && TRANSPORT_PACKAGE_TYPES.includes(packagingType['cbc:Value'])
                    ? markingCodeNode
                        ? markingCodeNode['cbc:Value']
                        : ''
                    : getText(gtinNode),
            lines: getText(safeGet(line, 'cbc:LineExtensionAmount')),
            rate: getText(safeGet(classification, 'cbc:Percent')),
            qty: getText(qtyNode),
            vatCategory: {value: vatCategoryValue, label: vatCategoryLabel},
            vat: getText(safeGet(classification, 'cbc:Percent') ?? 0),
            lineAmountWVat: lineAmountWithVat ? lineAmountWithVat['cbc:Value'] : '',
            price: getText(priceNode),
            code: markingCodeNode ? markingCodeNode['cbc:Value'] : '',
            isHsn: isHsn ?? '',
            serviceCategory: serviceCategory
                ? {
                      value: serviceCategory,
                      label: productCodeDescription ? productCodeDescription['cbc:Value'] : '',
                  }
                : undefined,
            isMarkedItem: !!markingCodeNode,
            ...(combinedCodeId && {combinedCodeId: combinedCodeId['cbc:Value']}),
            ...(isCombined && {isCombined: true}),
            ...(packagingType && {packagingType: packagingType['cbc:Value']}),
            ...(parent && {parent: parent['cbc:Value']}),
            ...(level && {level: level['cbc:Value']}),
        };
    });

    return {
        infoValues,
        pageValues,
        taxesAndTotal,
        invoiceLine,
    };
};
